import { TransactionFilters } from '@polybank/shared';
import { proxy, useSnapshot } from 'valtio';
import { devtools } from 'valtio/utils';
import { LocalStorageService, STORAGE_KEYS } from '@/utils/local-storage';

export interface FilterState {
  filters: TransactionFilters;
  appliedFilters: TransactionFilters;
  isFiltersOpen: boolean;
  pendingChanges: boolean;
  sortBy:
    | 'date-desc'
    | 'date-asc'
    | 'amount-desc'
    | 'amount-asc'
    | 'description-asc'
    | 'description-desc';
  // isSearching: boolean;
}

// Initial filter state
const initialFilters: TransactionFilters = {
  searchQuery: '',
  searchMode: 'contains',
  categories: undefined,
  accountFilter: undefined,
  dateRange: undefined,
  amountRange: undefined,
  transactionTypes: undefined,
  merchants: undefined,
};

// Storage key for sort preference
const STORAGE_KEY_SORT = 'polybank_sort_preference';

// Load persisted filters from localStorage
const loadPersistedFilters = (): TransactionFilters => {
  const persisted = LocalStorageService.get<TransactionFilters>(
    STORAGE_KEYS.TRANSACTION_FILTERS,
    initialFilters
  );

  // Hydrate date strings back to Date objects
  if (persisted.dateRange) {
    persisted.dateRange = {
      from: persisted.dateRange.from
        ? new Date(persisted.dateRange.from)
        : undefined,
      to: persisted.dateRange.to ? new Date(persisted.dateRange.to) : undefined,
    };
  }

  return persisted;
};

// Load persisted sort preference from localStorage
const loadPersistedSort = (): FilterState['sortBy'] => {
  return LocalStorageService.get<FilterState['sortBy']>(
    STORAGE_KEY_SORT,
    'date-desc'
  );
};

// Initialize with persisted data
const persistedFilters = loadPersistedFilters();
const persistedSort = loadPersistedSort();

// Create the proxy state
export const filterStore = proxy<FilterState>({
  filters: { ...persistedFilters },
  appliedFilters: { ...persistedFilters },
  isFiltersOpen: false,
  pendingChanges: false,
  sortBy: persistedSort,
  // isSearching: false,
});
devtools(filterStore, { name: 'filterStore', enabled: true });

// Actions for managing filter state
export const filterActions = {
  updateFilter: <K extends keyof TransactionFilters>(
    key: K,
    value: TransactionFilters[K],
    apply = false
  ) => {
    filterStore.filters[key] = value;
    filterStore.pendingChanges =
      JSON.stringify(filterStore.filters) !==
      JSON.stringify(filterStore.appliedFilters);
    if (apply) {
      filterStore.appliedFilters = { ...filterStore.filters };
    }
  },

  applyFilters: () => {
    filterStore.appliedFilters = { ...filterStore.filters };
    filterStore.pendingChanges = false;
    // Persist applied filters to localStorage
    LocalStorageService.set(
      STORAGE_KEYS.TRANSACTION_FILTERS,
      filterStore.appliedFilters
    );
  },

  clearAllFilters: () => {
    filterStore.filters = { ...initialFilters };
    filterStore.appliedFilters = { ...initialFilters };
    filterStore.pendingChanges = false;
    // Clear persisted filters
    LocalStorageService.set(STORAGE_KEYS.TRANSACTION_FILTERS, initialFilters);
  },

  resetFilters: () => {
    filterStore.filters = { ...filterStore.appliedFilters };
    filterStore.pendingChanges = false;
  },

  setFiltersOpen: (open: boolean) => {
    filterStore.isFiltersOpen = open;
  },

  updateSearchQuery: (query?: string) => {
    filterStore.filters.searchQuery = query;
    // Auto-apply search when filters panel is closed
    if (!filterStore.isFiltersOpen) {
      filterStore.appliedFilters.searchQuery = query;
      // Persist search query
      LocalStorageService.set(
        STORAGE_KEYS.TRANSACTION_FILTERS,
        filterStore.appliedFilters
      );
    } else {
      filterStore.pendingChanges =
        JSON.stringify(filterStore.filters) !==
        JSON.stringify(filterStore.appliedFilters);
    }
  },

  updateSearchMode: (mode: 'contains' | 'notContains') => {
    filterStore.filters.searchMode = mode;
    // Auto-apply search mode when filters panel is closed
    if (!filterStore.isFiltersOpen) {
      filterStore.appliedFilters.searchMode = mode;
      LocalStorageService.set(
        STORAGE_KEYS.TRANSACTION_FILTERS,
        filterStore.appliedFilters
      );
    } else {
      filterStore.pendingChanges =
        JSON.stringify(filterStore.filters) !==
        JSON.stringify(filterStore.appliedFilters);
    }
  },
  updateSort: (
    sortBy:
      | 'date-desc'
      | 'date-asc'
      | 'amount-desc'
      | 'amount-asc'
      | 'description-asc'
      | 'description-desc'
  ) => {
    filterStore.sortBy = sortBy;
    // Persist sort preference
    LocalStorageService.set(STORAGE_KEY_SORT, sortBy);
  },

  // Remove a specific filter and apply only that change (for summary chips)
  removeAndApplyFilter: <K extends keyof TransactionFilters>(key: K) => {
    if (key === 'searchQuery') {
      filterStore.filters['searchQuery'] = '';
      filterStore.appliedFilters['searchQuery'] = '';
    } else {
      filterStore.filters[key] = undefined as TransactionFilters[K];
      filterStore.appliedFilters[key] = undefined as TransactionFilters[K];
    }
    // Recalculate pending changes - other changes might still be pending
    filterStore.pendingChanges =
      JSON.stringify(filterStore.filters) !==
      JSON.stringify(filterStore.appliedFilters);
    // Persist the change
    LocalStorageService.set(
      STORAGE_KEYS.TRANSACTION_FILTERS,
      filterStore.appliedFilters
    );
  },
};

export const useTransactionFilters = () => {
  return useSnapshot(filterStore);
};
